import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Form, Input, Button, Card, message, Typography } from 'antd'
import { MailOutlined, ArrowLeftOutlined } from '@ant-design/icons'

const { Title, Text } = Typography

const API_URL = (import.meta.env as any).VITE_API_URL || 'http://localhost:3001/api'

export const ForgotPassword: React.FC = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const onFinish = async (values: { email: string }) => {
    setLoading(true)
    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: values.email }),
      })

      const data = await response.json()

      if (response.ok) {
        setEmailSent(true)
        message.success('Password reset instructions sent!')
      } else {
        message.error(data.message || 'Failed to send reset email')
      }
    } catch (error) {
      console.error('Forgot password error:', error)
      message.error('Failed to send reset email. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (emailSent) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        padding: '20px'
      }}>
        <Card
          style={{
            maxWidth: 500,
            width: '100%',
            borderRadius: 12,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
          }}
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MailOutlined style={{ fontSize: 40, color: '#ffffff' }} />
            </div>
            
            <Title level={2} style={{ marginBottom: 16 }}>
              Check Your Email
            </Title>
            
            <Text style={{ fontSize: 16, color: '#666', display: 'block', marginBottom: 24 }}>
              If an admin account exists with that email, we've sent password reset instructions. 
              Please check your inbox and follow the link to reset your password.
            </Text>
            
            <Text style={{ fontSize: 14, color: '#999', display: 'block', marginBottom: 24 }}>
              Didn't receive the email? Check your spam folder or try again in a few minutes.
            </Text>
            
            <Link to="/login">
              <Button type="primary" size="large" icon={<ArrowLeftOutlined />}>
                Back to Login
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      padding: '20px'
    }}>
      <Card
        style={{
          maxWidth: 450,
          width: '100%',
          borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Title level={2} style={{ marginBottom: 8 }}>
            Admin Password Reset
          </Title>
          <Text style={{ fontSize: 16, color: '#666' }}>
            Enter your admin email to receive reset instructions.
          </Text>
        </div>

        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          size="large"
          requiredMark={false}
        >
          <Form.Item
            name="email"
            label="Admin Email Address"
            rules={[
              { required: true, message: 'Please enter your email' },
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: '#999' }} />}
              placeholder="admin@example.com"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{
                height: 48,
                fontSize: 16,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                border: 'none'
              }}
            >
              Send Reset Instructions
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center' }}>
            <Link to="/login">
              <Button type="link" icon={<ArrowLeftOutlined />}>
                Back to Login
              </Button>
            </Link>
          </div>
        </Form>
      </Card>
    </div>
  )
}

export default ForgotPassword

